import React, { useEffect, useState } from 'react';
import {
  AutoComplete,
  Button,
  Checkbox,
  Dropdown,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Tooltip,
  Typography,
  message as toast,
} from 'antd';
import { MoreOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import AxiosUtils from 'utils/axios-utils';
import { API } from 'service/api-route';
import { colors } from 'theme/tokens';
import { useFeed } from './shared/context';
import { MOD_REASON_SUGGESTIONS, NO_PAGE_TOOLTIP, TIER_LABEL } from './shared/labels';
import { FeedAuthorView, FeedTier } from './shared/types';
import { errorCode, feedError, rankOf, useFeedSettingsOnce } from './shared/utils';

/**
 * Hộp lý do dùng chung cho ẩn / từ chối / xoá / hiện lại / duyệt / "Không vi phạm"
 * (03 §7.3) + hộp "Đánh dấu Quan trọng" + danh sách việc hợp lệ theo trạng thái.
 * hide/reject/delete gửi ModerateDto { reason (3–500), notifyAuthor }; unhide/approve gửi
 * NoteDto { note? } — "Không vi phạm" chính là approve của bài đang hiện có cờ (D7).
 */

export type ModKind = 'post' | 'comment';
export type ModAction = 'hide' | 'reject' | 'delete' | 'unhide' | 'approve' | 'clear';
export type ContentActionKey = ModAction | 'pin' | 'unpin' | 'important' | 'unimportant';

interface ModTarget {
  kind: ModKind;
  id: number;
  action: ModAction;
  title?: string;
}

const NEEDS_REASON: ModAction[] = ['hide', 'reject', 'delete'];

const noun = (kind: ModKind) => (kind === 'post' ? 'bài' : 'bình luận');

function modTitle(t: ModTarget): string {
  if (t.title) return t.title;
  const n = `${noun(t.kind)} #${t.id}`;
  switch (t.action) {
    case 'hide':
      return `Ẩn ${n}`;
    case 'reject':
      return `Từ chối ${n}`;
    case 'delete':
      return `Xoá ${n}`;
    case 'unhide':
      return `Hiện lại ${n}`;
    case 'approve':
      return `Duyệt ${n}`;
    default:
      return `Không vi phạm — ${n}`;
  }
}

function okText(t: ModTarget): string {
  switch (t.action) {
    case 'hide':
      return t.kind === 'post' ? 'Ẩn bài' : 'Ẩn bình luận';
    case 'reject':
      return 'Từ chối';
    case 'delete':
      return t.kind === 'post' ? 'Xoá bài' : 'Xoá bình luận';
    case 'unhide':
      return 'Hiện lại';
    case 'approve':
      return 'Duyệt';
    default:
      return 'Không vi phạm';
  }
}

function doneText(t: ModTarget): string {
  const n = `${noun(t.kind)} #${t.id}`;
  switch (t.action) {
    case 'hide':
      return `Đã ẩn ${n}.`;
    case 'reject':
      return `Đã từ chối ${n}.`;
    case 'delete':
      return `Đã xoá ${n}.`;
    case 'unhide':
      return `Đã hiện lại ${n}.`;
    case 'approve':
      return `Đã duyệt ${n}.`;
    default:
      return `Đã đánh dấu ${n} không vi phạm.`;
  }
}

const endpoint = (t: ModTarget) =>
  `${t.kind === 'post' ? API.feedAdminPosts : API.feedAdminComments}/${t.id}/${
    t.action === 'clear' ? 'approve' : t.action
  }`;

const ModerateModal = ({
  target,
  onClose,
  onDone,
}: {
  target: ModTarget | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const [reason, setReason] = useState('');
  const [notify, setNotify] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setReason('');
    setNotify(true);
  }, [target]);

  if (!target) return null;
  const needsReason = NEEDS_REASON.includes(target.action);
  const trimmed = reason.trim();
  const invalid = needsReason ? trimmed.length < 3 || trimmed.length > 500 : trimmed.length > 500;

  const submit = async () => {
    if (invalid) return;
    setSaving(true);
    try {
      const body = needsReason ? { reason: trimmed, notifyAuthor: notify } : trimmed ? { note: trimmed } : {};
      await AxiosUtils.post(endpoint(target), body);
      toast.success(doneText(target));
      onClose();
      onDone();
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setSaving(false);
    }
  };

  const hint =
    target.action === 'hide' || target.action === 'reject'
      ? 'Tác giả sẽ thấy lý do này.'
      : target.action === 'delete'
      ? `${target.kind === 'post' ? 'Bài' : 'Bình luận'} biến mất khỏi Bảng tin của mọi người. Nội dung vẫn lưu trong Nhật ký.`
      : target.action === 'clear'
      ? 'Xoá cờ "Cần xem". Không gửi thông báo cho tác giả.'
      : 'Ghi chú lưu vào Nhật ký (không bắt buộc).';

  return (
    <Modal
      open
      width={560}
      title={modTitle(target)}
      onCancel={onClose}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onClose}>
          Huỷ
        </Button>,
        <Button
          key="ok"
          type="primary"
          danger={target.action === 'delete'}
          loading={saving}
          disabled={invalid}
          onClick={submit}
        >
          {okText(target)}
        </Button>,
      ]}
    >
      <Form layout="vertical">
        <Form.Item
          label={needsReason ? 'Lý do' : 'Ghi chú'}
          required={needsReason}
          extra={hint}
          validateStatus={needsReason && reason && trimmed.length < 3 ? 'error' : undefined}
          help={needsReason && reason && trimmed.length < 3 ? 'Lý do tối thiểu 3 ký tự.' : undefined}
        >
          {needsReason ? (
            <AutoComplete
              value={reason}
              onChange={(v) => setReason(String(v || ''))}
              options={MOD_REASON_SUGGESTIONS.map((value) => ({ value }))}
              filterOption={(input, option) =>
                String(option?.value || '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            >
              <Input.TextArea
                autoSize={{ minRows: 2, maxRows: 6 }}
                maxLength={500}
                showCount
                placeholder="Chọn gợi ý hoặc tự nhập"
              />
            </AutoComplete>
          ) : (
            <Input.TextArea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              autoSize={{ minRows: 2, maxRows: 5 }}
              maxLength={500}
              showCount
            />
          )}
        </Form.Item>
        {needsReason && (
          <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)}>
            Gửi thông báo cho tác giả
          </Checkbox>
        )}
      </Form>
    </Modal>
  );
};

// ------------------------------------------------------------------ Quan trọng (P10/P11)

const ImportantModal = ({
  postId,
  onClose,
  onDone,
}: {
  postId: number | null;
  onClose: () => void;
  onDone: () => void;
}) => {
  const { access } = useFeed();
  const settings = useFeedSettingsOnce(!!postId);
  const defaultDays = settings?.importantDefaultDays || access.limits?.importantDefaultDays || 7;
  const [days, setDays] = useState<number>(defaultDays);
  const [notify, setNotify] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDays(defaultDays);
    setNotify(false);
  }, [postId, defaultDays]);

  if (!postId) return null;
  const perDay = settings?.importantPushPerDay;

  const submit = async () => {
    setSaving(true);
    try {
      await AxiosUtils.post(`${API.feedPosts}/${postId}/important`, { days, notify });
      toast.success(`Đã đánh dấu Quan trọng đến ${dayjs().add(days, 'day').format('DD/MM')}.`);
      onClose();
      onDone();
    } catch (err) {
      toast.error(feedError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      width={560}
      title={`Đánh dấu Quan trọng · bài #${postId}`}
      onCancel={onClose}
      onOk={submit}
      okText="Đánh dấu Quan trọng"
      cancelText="Huỷ"
      confirmLoading={saving}
      destroyOnHidden
    >
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        <Space wrap>
          <span>Hiện ở đầu Bảng tin trong</span>
          <InputNumber min={1} max={30} value={days} onChange={(v) => setDays(Number(v) || 1)} addonAfter="ngày" />
        </Space>
        <Checkbox checked={notify} onChange={(e) => setNotify(e.target.checked)}>
          Gửi thông báo đẩy cho mọi người
          {perDay !== undefined ? ` (tối đa ${perDay} bài/ngày, mỗi bài một lần)` : ' (mỗi bài một lần)'}
        </Checkbox>
        <Typography.Text type="secondary" style={{ fontSize: 13 }}>
          Chỉ bài đăng tên HP Media mới đánh dấu Quan trọng được. Người xem thấy nút "Tôi đã đọc".
        </Typography.Text>
      </Space>
    </Modal>
  );
};

// ------------------------------------------------------------------ việc theo trạng thái

export interface ActionableContent {
  id: number;
  status: string;
  deletedAt?: string | null;
  needsReview?: boolean;
  hiddenByTier?: FeedTier | null;
  author?: FeedAuthorView | null;
  isPinned?: boolean;
  isImportant?: boolean;
  importantUntil?: string | null;
}

export interface ContentActionItem {
  key: ContentActionKey;
  label: string;
  danger?: boolean;
  primary?: boolean;
  disabled?: boolean;
  tooltip?: string;
}

const isImportantNow = (row: ActionableContent) =>
  !!row.isImportant && (!row.importantUntil || dayjs(row.importantUntil).isAfter(dayjs()));

/** Việc hợp lệ với trạng thái hiện tại (03 §7.3). Nút mờ sẵn theo luật thứ bậc (§2). */
export function contentActions(
  kind: ModKind,
  row: ActionableContent,
  ctx: { myRank: number; canMarkImportant: boolean }
): ContentActionItem[] {
  if (row.deletedAt) return [];
  const pageLocked = kind === 'post' && row.author?.type === 'PAGE' && ctx.myRank < 2;
  const lock = (item: ContentActionItem): ContentActionItem =>
    pageLocked ? { ...item, disabled: true, tooltip: NO_PAGE_TOOLTIP } : item;
  const hide = lock({ key: 'hide', label: 'Ẩn' });
  const del = lock({ key: 'delete', label: 'Xoá', danger: true });
  const items: ContentActionItem[] = [];
  switch (row.status) {
    case 'PUBLISHED':
      if (row.needsReview) items.push({ key: 'clear', label: 'Không vi phạm', primary: true });
      items.push(hide, del);
      if (kind === 'post') {
        items.push(row.isPinned ? { key: 'unpin', label: 'Bỏ ghim' } : { key: 'pin', label: 'Ghim' });
        if (row.author?.type === 'PAGE' && ctx.canMarkImportant) {
          items.push(
            isImportantNow(row)
              ? { key: 'unimportant', label: 'Bỏ Quan trọng' }
              : { key: 'important', label: 'Đánh dấu Quan trọng' }
          );
        }
      }
      break;
    case 'PENDING_REVIEW':
      items.push(
        { key: 'approve', label: 'Duyệt', primary: true },
        lock({ key: 'reject', label: 'Từ chối' }),
        hide,
        del
      );
      break;
    case 'HIDDEN': {
      const higher = rankOf(row.hiddenByTier) > ctx.myRank;
      items.push(
        higher
          ? {
              key: 'unhide',
              label: 'Hiện lại',
              disabled: true,
              tooltip: `${kind === 'post' ? 'Bài' : 'Bình luận'} do ${
                row.hiddenByTier ? TIER_LABEL[row.hiddenByTier] : 'người bậc cao hơn'
              } ẩn — chỉ người cùng bậc trở lên hiện lại được.`,
            }
          : { key: 'unhide', label: 'Hiện lại' },
        del
      );
      break;
    }
    default:
      // REJECTED, FAILED
      items.push(del);
  }
  return items;
}

/**
 * Chạy một việc trên bài/bình luận: mở hộp lý do khi cần, ghim/bỏ Quan trọng làm ngay.
 * Trả `modals` để đặt vào cây (một lần mỗi nơi dùng).
 */
export function useContentActions() {
  const { afterAction } = useFeed();
  const [target, setTarget] = useState<ModTarget | null>(null);
  const [importantId, setImportantId] = useState<number | null>(null);

  const run = async (kind: ModKind, id: number, key: ContentActionKey, title?: string) => {
    if (key === 'pin' || key === 'unpin') {
      try {
        await AxiosUtils.post(`${API.feedAdminPosts}/${id}/${key}`, {});
        toast.success(key === 'pin' ? 'Đã ghim bài.' : 'Đã bỏ ghim.');
        afterAction();
      } catch (err) {
        // FEED_PIN_LIMIT: "Chỉ ghim tối đa 3 bài. Bỏ ghim một bài trước."
        toast.error(feedError(err));
      }
      return;
    }
    if (key === 'important') {
      setImportantId(id);
      return;
    }
    if (key === 'unimportant') {
      try {
        await AxiosUtils._delete(`${API.feedPosts}/${id}/important`, undefined);
        toast.success('Đã bỏ Quan trọng.');
        afterAction();
      } catch (err) {
        toast.error(feedError(err));
      }
      return;
    }
    setTarget({ kind, id, action: key, title });
  };

  const modals = (
    <>
      <ModerateModal target={target} onClose={() => setTarget(null)} onDone={afterAction} />
      <ImportantModal postId={importantId} onClose={() => setImportantId(null)} onDone={afterAction} />
    </>
  );

  return { run, modals };
}

/** Nút "⋯" trong bảng/thẻ. */
export const ContentActionsMenu = ({
  items,
  onRun,
  size = 'middle',
}: {
  items: ContentActionItem[];
  onRun: (key: ContentActionKey) => void;
  size?: 'small' | 'middle';
}) => (
  <Dropdown
    trigger={['click']}
    disabled={!items.length}
    menu={{
      items: items.map((item) => ({
        key: item.key,
        danger: item.danger,
        disabled: item.disabled,
        label: item.tooltip ? (
          <Tooltip title={item.tooltip} placement="left">
            <span>{item.label}</span>
          </Tooltip>
        ) : (
          item.label
        ),
      })),
      onClick: ({ key }) => onRun(key as ContentActionKey),
    }}
  >
    <Button size={size} icon={<MoreOutlined />} aria-label="Thao tác" />
  </Dropdown>
);

/** Hàng nút (ngăn chi tiết, thẻ Cần xem). */
export const ContentActionButtons = ({
  items,
  onRun,
  size = 'middle',
}: {
  items: ContentActionItem[];
  onRun: (key: ContentActionKey) => void;
  size?: 'small' | 'middle';
}) => {
  if (!items.length) return <span style={{ color: colors.textMuted, fontSize: 13 }}>Không có thao tác.</span>;
  return (
    <Space wrap size={8}>
      {items.map((item) => {
        const button = (
          <Button
            key={item.key}
            size={size}
            type={item.primary ? 'primary' : 'default'}
            danger={item.danger}
            disabled={item.disabled}
            onClick={() => onRun(item.key)}
          >
            {item.label}
          </Button>
        );
        return item.tooltip ? (
          <Tooltip key={item.key} title={item.tooltip}>
            <span>{button}</span>
          </Tooltip>
        ) : (
          button
        );
      })}
    </Space>
  );
};

/** Có phải lỗi "đã có người khác xử lý" không (để tải lại thay vì báo lỗi đỏ). */
export const isAlreadyHandled = (err: any) =>
  ['FEED_REPORT_ALREADY_RESOLVED', 'FEED_INVALID_STATE'].includes(errorCode(err));

export default ModerateModal;
